var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import hondaRestApi from '../apis/hondaRestApi';
import { getDefaultIncentiveOfferIDs } from './offers';
import { getStateFromAction } from '../utils';
import { LEASE_ANNUAL_KM_ALLOWANCE } from '../../utils/constants';
export var primaryCalculatorId = 'primary';
export var defaultDraftCalculatorId = 'modalDraft';
export var PaymentMethod;
(function (PaymentMethod) {
    PaymentMethod["lease"] = "lease";
    PaymentMethod["finance"] = "finance";
    PaymentMethod["cash"] = "cash";
})(PaymentMethod || (PaymentMethod = {}));
export var PaymentFrequency;
(function (PaymentFrequency) {
    PaymentFrequency["weekly"] = "weekly";
    PaymentFrequency["biweekly"] = "biweekly";
    PaymentFrequency["monthly"] = "monthly";
})(PaymentFrequency || (PaymentFrequency = {}));
export var CalculatorFields;
(function (CalculatorFields) {
    CalculatorFields["DownPaymentAmount"] = "downPaymentAmount";
    CalculatorFields["TradeInValueAmount"] = "tradeInValueAmount";
    CalculatorFields["TradeInOwingAmount"] = "tradeInOwingAmount";
})(CalculatorFields || (CalculatorFields = {}));
export var CalculatorUrlKeys;
(function (CalculatorUrlKeys) {
    CalculatorUrlKeys["paymentMethod"] = "paymentMethod";
    CalculatorUrlKeys["paymentFrequency"] = "paymentFrequency";
    CalculatorUrlKeys["termAndRate"] = "termAndRate";
    CalculatorUrlKeys["downPayment"] = "downPayment";
    CalculatorUrlKeys["tradeInValue"] = "tradeInValue";
    CalculatorUrlKeys["tradeInOwing"] = "tradeInOwing";
    CalculatorUrlKeys["warrantyKey"] = "warrantyKey";
})(CalculatorUrlKeys || (CalculatorUrlKeys = {}));
export var calculatorInitialState = {
    overriddenIsSellingProvince: false,
    includeFees: false,
    paymentMethod: PaymentMethod.lease,
    paymentFrequency: PaymentFrequency.monthly,
    termAndRate: 60,
    leaseAnnualKmAllowance: LEASE_ANNUAL_KM_ALLOWANCE,
    additionalLeaseAnnualKmAllowance: 0,
    downPaymentAmount: 0,
    tradeInValueAmount: 0,
    tradeInOwingAmount: 0,
    incentiveOfferIds: [],
    loyaltyOfferKey: '',
    nonStackableOfferSelected: false,
    warrantyKey: '',
    warrantyAmount: 0,
};
export var initialState = (_a = {},
    _a[primaryCalculatorId] = __assign({}, calculatorInitialState),
    _a);
export var createCalculator = createAction('financialCalculators/createCalculator');
export var deleteCalculator = createAction('financialCalculators/deleteCalculator');
export var setIncludeFees = createAction('financialCalculators/includeFees');
export var setOverriddenIsSellingProvince = createAction('financialCalculators/overriddenIsSellingProvince');
export var setPaymentMethod = createAction('financialCalculators/paymentMethod');
export var setPaymentFrequency = createAction('financialCalculators/paymentFrequency');
export var setTermAndRate = createAction('financialCalculators/termAndRate');
export var setLeaseAnnualKmAllowance = createAction('financialCalculators/leaseAnnualKmAllowance');
export var setAdditionalLeaseAnnualKmAllowance = createAction('financialCalculators/additionalLeaseAnnualKmAllowance');
export var offerSelectionChanged = createAction('financialCalculators/offerSelectionChanged');
export var offersSelectionChanged = createAction('financialCalculators/offersSelectionChanged');
export var loyaltyOfferChanged = createAction('financialCalculators/loyaltyOfferChanged');
export var nonStackableOfferSelect = createAction('financialCalculators/nonStackableOfferSelect');
export var resetOffers = createAction('financialCalculators/resetOffers');
export var hydrateFromCalculator = createAction('financialCalculators/hydrateFromCalculator');
export var urlParsedOnInitialLoad = createAction('financialCalculators/urlParsedOnInitialLoad');
export var setPrimaryCalculatorPaymentMethod = createAction('financialCalculators/primaryPaymentMethod');
export var setPrimaryCalculatorPaymentFrequency = createAction('financialCalculators/primaryPaymentFrequency');
export var setPrimaryCalculatorIncludeFees = createAction('financialCalculators/primaryIncludeFees');
export var setPrimaryCalculatorOverriddenIsSellingProvince = createAction('financialCalculators/primaryOverriddenIsSellingProvince');
export var setDownPaymentAmount = createAction('financialCalculators/downPaymentAmount');
export var setTradeInValueAmount = createAction('financialCalculators/tradeInValueAmount');
export var setTradeInOwingAmount = createAction('financialCalculators/tradeInOwingAmount');
export var warrantySelected = createAction('financialCalculators/warrantyKeySelected');
export var primaryCalculatorWarrantySelected = createAction('financialCalculators/primaryWarrantySelected');
export var draftCalculatorWarrantySelected = createAction('financialCalculators/draftWarrantySelected');
export var updateCalculatorField = createAction('financialCalculators/updateCalculatorField');
var financialCalculatorsReducer = createReducer(initialState, function (builder) {
    builder
        .addCase(createCalculator, function (state, action) {
        state[action.payload] = __assign({}, calculatorInitialState);
    })
        .addCase(setIncludeFees, function (state, action) {
        var _a = action.payload, id = _a.id, includeFees = _a.includeFees;
        var calculator = state[id];
        calculator.includeFees = includeFees;
    })
        .addCase(setPrimaryCalculatorIncludeFees, function (state, action) {
        var calculator = state[primaryCalculatorId];
        calculator.includeFees = action.payload;
    })
        .addCase(setOverriddenIsSellingProvince, function (state, action) {
        var _a = action.payload, id = _a.id, overriddenIsSellingProvince = _a.overriddenIsSellingProvince;
        var calculator = state[id];
        calculator.overriddenIsSellingProvince = overriddenIsSellingProvince;
    })
        .addCase(setPrimaryCalculatorOverriddenIsSellingProvince, function (state, action) {
        var calculator = state[primaryCalculatorId];
        calculator.overriddenIsSellingProvince = action.payload;
    })
        .addCase(setPaymentMethod, function (state, action) {
        var _a = action.payload, id = _a.id, paymentMethod = _a.paymentMethod;
        var calculator = state[id];
        calculator.paymentMethod = paymentMethod;
    })
        .addCase(setPrimaryCalculatorPaymentMethod, function (state, action) {
        var calculator = state[primaryCalculatorId];
        calculator.paymentMethod = action.payload;
    })
        .addCase(setPaymentFrequency, function (state, action) {
        var _a = action.payload, id = _a.id, paymentFrequency = _a.paymentFrequency;
        var calculator = state[id];
        calculator.paymentFrequency = paymentFrequency;
    })
        .addCase(setPrimaryCalculatorPaymentFrequency, function (state, action) {
        var calculator = state[primaryCalculatorId];
        calculator.paymentFrequency = action.payload;
    })
        .addCase(setTermAndRate, function (state, action) {
        var _a = action.payload, id = _a.id, termAndRate = _a.termAndRate;
        var calculator = state[id];
        calculator.termAndRate = termAndRate;
    })
        .addCase(setDownPaymentAmount, function (state, action) {
        var _a = action.payload, id = _a.id, downPaymentAmount = _a.downPaymentAmount;
        var calculator = state[id];
        calculator.downPaymentAmount = downPaymentAmount;
    })
        .addCase(setTradeInValueAmount, function (state, action) {
        var _a = action.payload, id = _a.id, tradeInValueAmount = _a.tradeInValueAmount;
        var calculator = state[id];
        calculator.tradeInValueAmount = tradeInValueAmount;
    })
        .addCase(setTradeInOwingAmount, function (state, action) {
        var _a = action.payload, id = _a.id, tradeInOwingAmount = _a.tradeInOwingAmount;
        var calculator = state[id];
        calculator.tradeInOwingAmount = tradeInOwingAmount;
    })
        .addCase(setLeaseAnnualKmAllowance, function (state, action) {
        var _a = action.payload, id = _a.id, leaseAnnualKmAllowance = _a.leaseAnnualKmAllowance;
        var calculator = state[id];
        calculator.leaseAnnualKmAllowance = leaseAnnualKmAllowance;
    })
        .addCase(setAdditionalLeaseAnnualKmAllowance, function (state, action) {
        var _a = action.payload, id = _a.id, additionalLeaseAnnualKmAllowance = _a.additionalLeaseAnnualKmAllowance;
        var calculator = state[id];
        calculator.additionalLeaseAnnualKmAllowance = additionalLeaseAnnualKmAllowance;
    })
        .addCase(warrantySelected, function (state, action) {
        var _a = action.payload, id = _a.id, warrantyKey = _a.warrantyKey;
        var calculator = state[id];
        calculator.warrantyKey = warrantyKey;
    })
        .addCase(primaryCalculatorWarrantySelected, function (state, action) {
        var calculator = state[primaryCalculatorId];
        calculator.warrantyKey = action.payload.key;
        calculator.warrantyAmount = action.payload.price;
        calculator.checkedModalWarrantyCard = action.payload.checkedModalWarrantyCard;
        calculator.checkedModalWarrantyKey = action.payload.checkedModalWarrantyKey;
    })
        .addCase(draftCalculatorWarrantySelected, function (state, action) {
        var calculator = state[defaultDraftCalculatorId];
        calculator.warrantyKey = action.payload.key;
        calculator.warrantyAmount = action.payload.price;
    })
        .addCase(hydrateFromCalculator, function (state, action) {
        var _a, _b;
        var _c = action.payload, destinationId = _c.destinationId, sourceId = _c.sourceId, offerIds = _c.offerIds, loyaltyOffer = _c.loyaltyOffer;
        var sourceCalculator = state[sourceId];
        var incentiveOfferIds = offerIds
            ? __spreadArray([], new Set(__spreadArray(__spreadArray([], offerIds, true), ((_a = sourceCalculator === null || sourceCalculator === void 0 ? void 0 : sourceCalculator.incentiveOfferIds) !== null && _a !== void 0 ? _a : []), true)), true) : (_b = sourceCalculator === null || sourceCalculator === void 0 ? void 0 : sourceCalculator.incentiveOfferIds) !== null && _b !== void 0 ? _b : [];
        state[destinationId] = __assign(__assign({}, (sourceCalculator || calculatorInitialState)), { incentiveOfferIds: incentiveOfferIds.filter(Boolean), loyaltyOfferKey: (sourceCalculator === null || sourceCalculator === void 0 ? void 0 : sourceCalculator.loyaltyOfferKey) || loyaltyOffer || '' });
    })
        .addCase(deleteCalculator, function (state, action) {
        delete state[action.payload];
    })
        .addCase(urlParsedOnInitialLoad, function (state, action) {
        var _a;
        var primaryCalculatorState = state[primaryCalculatorId];
        return __assign(__assign({}, state), (_a = {}, _a[primaryCalculatorId] = __assign(__assign({}, primaryCalculatorState), action.payload), _a));
    })
        .addCase(offerSelectionChanged, function (state, action) {
        var _a = action.payload, calculatorId = _a.calculatorId, offerId = _a.offerId, isSelected = _a.isSelected;
        var calculator = state[calculatorId];
        if (isSelected) {
            calculator.incentiveOfferIds = __spreadArray([], new Set(__spreadArray(__spreadArray([], calculator.incentiveOfferIds, true), [offerId], false)), true).filter(Boolean);
        }
        else {
            calculator.incentiveOfferIds = calculator.incentiveOfferIds.filter(function (id) { return id && id !== offerId; });
        }
    })
        .addCase(loyaltyOfferChanged, function (state, action) {
        var _a = action.payload, calculatorId = _a.calculatorId, offerKey = _a.offerKey;
        var calculator = state[calculatorId];
        calculator.loyaltyOfferKey = offerKey;
    })
        .addCase(resetOffers, function (state, action) {
        var calculatorId = action.payload.calculatorId;
        var calculator = state[calculatorId];
        calculator.incentiveOfferIds = calculatorInitialState.incentiveOfferIds;
    })
        .addCase(offersSelectionChanged, function (state, action) {
        var _a = action.payload, calculatorId = _a.calculatorId, offerIds = _a.offerIds;
        var calculator = state[calculatorId];
        calculator.incentiveOfferIds = offerIds.filter(Boolean);
    })
        .addCase(nonStackableOfferSelect, function (state, action) {
        var _a = action.payload, calculatorId = _a.calculatorId, value = _a.value;
        var calculator = state[calculatorId];
        calculator.nonStackableOfferSelected = value;
    })
        .addCase(updateCalculatorField, function (state, action) {
        var _a;
        var _b = action.payload, id = _b.id, field = _b.field;
        var calculator = state[id];
        return __assign(__assign({}, state), (_a = {}, _a[id] = __assign(__assign({}, calculator), field), _a));
    })
        .addMatcher(hondaRestApi.endpoints.getPayment.matchFulfilled, function (state, action) {
        var _a;
        var _b, _c, _d, _e;
        var primaryCalculator = state[primaryCalculatorId];
        return __assign(__assign({}, state), (_a = {}, _a[primaryCalculatorId] = __assign(__assign({}, primaryCalculator), { warrantyAmount: ((_e = (_d = (_c = (_b = action.payload) === null || _b === void 0 ? void 0 : _b.transformedData) === null || _c === void 0 ? void 0 : _c.warranties) === null || _d === void 0 ? void 0 : _d.find(function (w) { return w.businessKey === primaryCalculator.warrantyKey; })) === null || _e === void 0 ? void 0 : _e.priceTotal) || 0 }), _a));
    })
        .addMatcher(hondaRestApi.endpoints.getOffers.matchFulfilled, function (state, action) {
        var _a;
        var _b, _c, _d, _e;
        var primaryCalculator = state[primaryCalculatorId];
        var paymentMethodIncentiveOffers = (_d = (_c = (_b = action === null || action === void 0 ? void 0 : action.payload) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.incentiveOffers[primaryCalculator.paymentMethod]) !== null && _d !== void 0 ? _d : [];
        var skipNonStackableOffers = getStateFromAction(action, 'offerOptions').canSkipDefaultNonStackableOffers;
        var selectedIncentiveOfferIds = getDefaultIncentiveOfferIDs({
            incentiveOffers: paymentMethodIncentiveOffers,
            previousIncentiveOfferIds: (_e = primaryCalculator === null || primaryCalculator === void 0 ? void 0 : primaryCalculator.incentiveOfferIds) !== null && _e !== void 0 ? _e : [],
            skipNonStackableOffers: skipNonStackableOffers,
        });
        return __assign(__assign({}, state), (_a = {}, _a[primaryCalculatorId] = __assign(__assign({}, primaryCalculator), { incentiveOfferIds: selectedIncentiveOfferIds }), _a));
    });
});
export var selectFinancialCalculator = function (state, id) { return state.financialCalculators[id]; };
export var selectAdditionalLeaseAnnualKmAllowance = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.additionalLeaseAnnualKmAllowance; });
export var selectLeaseAnnualKmAllowance = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.leaseAnnualKmAllowance; });
export var selectTermAndRate = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.termAndRate; });
export var selectPaymentFrequency = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.paymentFrequency; });
export var selectIsPaymentMethodCash = createSelector([selectFinancialCalculator], function (calculator) { return (calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod) === PaymentMethod.cash; });
export var selectIsPaymentMethodLease = createSelector([selectFinancialCalculator], function (calculator) { return (calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod) === PaymentMethod.lease; });
export var selectPaymentMethod = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod; });
export var selectDownPaymentAmount = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.downPaymentAmount; });
export var selectTradeInValueAmount = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.tradeInValueAmount; });
export var selectTradeInOwingAmount = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.tradeInOwingAmount; });
export var selectIncentiveOffers = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.incentiveOfferIds; });
export var selectLoyaltyOffer = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.loyaltyOfferKey; });
export var selectNonStackableOfferSelected = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.nonStackableOfferSelected; });
export var selectWarrantyKey = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.warrantyKey; });
export var selectCheckedModalWarrantyCard = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.checkedModalWarrantyCard; });
export var selectCheckedModalWarrantyKey = createSelector([selectFinancialCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.checkedModalWarrantyKey; });
var generatePaymentOptions = function (calculator, offersState, isPrimary) { return ({
    downPaymentAmount: (calculator === null || calculator === void 0 ? void 0 : calculator.downPaymentAmount) || calculatorInitialState.downPaymentAmount,
    incentiveOfferIds: (isPrimary ? offersState === null || offersState === void 0 ? void 0 : offersState.incentive : calculator === null || calculator === void 0 ? void 0 : calculator.incentiveOfferIds) || calculatorInitialState.incentiveOfferIds,
    includeFees: (calculator === null || calculator === void 0 ? void 0 : calculator.includeFees) || calculatorInitialState.includeFees,
    leaseAdditionalAnnualKm: (calculator === null || calculator === void 0 ? void 0 : calculator.additionalLeaseAnnualKmAllowance) || calculatorInitialState.additionalLeaseAnnualKmAllowance,
    leaseAnnualKmAllowance: (calculator === null || calculator === void 0 ? void 0 : calculator.leaseAnnualKmAllowance) || calculatorInitialState.leaseAnnualKmAllowance,
    loyaltyOfferKey: (isPrimary ? offersState === null || offersState === void 0 ? void 0 : offersState.loyalty : calculator === null || calculator === void 0 ? void 0 : calculator.loyaltyOfferKey) || calculatorInitialState.loyaltyOfferKey,
    overriddenIsSellingProvince: (calculator === null || calculator === void 0 ? void 0 : calculator.overriddenIsSellingProvince) || calculatorInitialState.overriddenIsSellingProvince,
    paymentFrequency: (calculator === null || calculator === void 0 ? void 0 : calculator.paymentFrequency) || calculatorInitialState.paymentFrequency,
    paymentMethod: (calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod) || calculatorInitialState.paymentMethod,
    termAndRate: (calculator === null || calculator === void 0 ? void 0 : calculator.termAndRate) || calculatorInitialState.termAndRate,
    tradeInOwingAmount: (calculator === null || calculator === void 0 ? void 0 : calculator.tradeInOwingAmount) || calculatorInitialState.tradeInOwingAmount,
    tradeInValueAmount: (calculator === null || calculator === void 0 ? void 0 : calculator.tradeInValueAmount) || calculatorInitialState.tradeInValueAmount,
    warrantyAmount: (calculator === null || calculator === void 0 ? void 0 : calculator.warrantyAmount) || 0,
}); };
var generateCashQueryStringParams = function (calculator) {
    var _a;
    return (_a = {},
        _a[CalculatorUrlKeys.paymentMethod] = calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod,
        _a[CalculatorUrlKeys.tradeInValue] = calculator === null || calculator === void 0 ? void 0 : calculator.tradeInValueAmount,
        _a[CalculatorUrlKeys.warrantyKey] = calculator === null || calculator === void 0 ? void 0 : calculator.warrantyKey,
        _a);
};
var generateLeaseFinanceQueryStringParams = function (calculator) {
    var _a;
    return (_a = {},
        _a[CalculatorUrlKeys.paymentMethod] = calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod,
        _a[CalculatorUrlKeys.paymentFrequency] = calculator === null || calculator === void 0 ? void 0 : calculator.paymentFrequency,
        _a[CalculatorUrlKeys.tradeInValue] = calculator === null || calculator === void 0 ? void 0 : calculator.tradeInValueAmount,
        _a[CalculatorUrlKeys.tradeInOwing] = calculator === null || calculator === void 0 ? void 0 : calculator.tradeInOwingAmount,
        _a[CalculatorUrlKeys.downPayment] = calculator === null || calculator === void 0 ? void 0 : calculator.downPaymentAmount,
        _a[CalculatorUrlKeys.termAndRate] = calculator === null || calculator === void 0 ? void 0 : calculator.termAndRate,
        _a[CalculatorUrlKeys.warrantyKey] = calculator === null || calculator === void 0 ? void 0 : calculator.warrantyKey,
        _a);
};
var generateQueryString = function (params) {
    try {
        var searchParams_1 = new URLSearchParams();
        Object.entries(params).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            if (value) {
                searchParams_1.set(key, value.toString());
            }
        });
        return searchParams_1.toString();
    }
    catch (error) {
        return '';
    }
};
// keep placeholder object reference stable
var emptyOffer = {
    incentive: [],
    loyalty: '',
};
var selectorForOffers = function (state, id) {
    var _a, _b, _c, _d, _e;
    if (id === void 0) { id = primaryCalculatorId; }
    var transmissionKey = (_a = state === null || state === void 0 ? void 0 : state.currentVehicle) === null || _a === void 0 ? void 0 : _a.transmissionKey;
    var paymentMethod = ((_b = state.financialCalculators[id]) === null || _b === void 0 ? void 0 : _b.paymentMethod) || PaymentMethod.lease;
    return (_e = (_d = (_c = state.offers) === null || _c === void 0 ? void 0 : _c[transmissionKey]) === null || _d === void 0 ? void 0 : _d[paymentMethod]) !== null && _e !== void 0 ? _e : emptyOffer;
};
export var selectPaymentOptions = createSelector(selectFinancialCalculator, selectorForOffers, function (state, id) {
    if (id === void 0) { id = primaryCalculatorId; }
    return id === primaryCalculatorId;
}, function (calculator, offersState, isPrimary) { return generatePaymentOptions(calculator, offersState, isPrimary); });
export var selectCashPaymentQueryStringParams = createSelector([selectFinancialCalculator], function (calculator) {
    return generateCashQueryStringParams(calculator);
});
export var selectLeaseFinancePaymentQueryStringParams = createSelector([selectFinancialCalculator], function (calculator) {
    return generateLeaseFinanceQueryStringParams(calculator);
});
export var selectCalculatorQueryStringParams = createSelector([selectIsPaymentMethodCash, selectCashPaymentQueryStringParams, selectLeaseFinancePaymentQueryStringParams], function (isCashPayment, cashQuerystringParams, leaseFinanceQueryStringParams) {
    return isCashPayment ? cashQuerystringParams : leaseFinanceQueryStringParams;
});
export var selectCalculatorQueryString = createSelector([selectCalculatorQueryStringParams], function (calculatorQueryStringParams) { return generateQueryString(calculatorQueryStringParams); });
var updateQueryStringFromCalculator = function (state) {
    var url = new URL(window.location.href);
    url.searchParams.set(CalculatorUrlKeys.paymentMethod, state.paymentMethod);
    url.searchParams.set(CalculatorUrlKeys.tradeInValue, state[CalculatorFields.TradeInValueAmount].toString());
    url.searchParams.set(CalculatorUrlKeys.warrantyKey, state.warrantyKey);
    if (state.paymentMethod !== PaymentMethod.cash) {
        url.searchParams.set(CalculatorUrlKeys.downPayment, state[CalculatorFields.DownPaymentAmount].toString());
        url.searchParams.set(CalculatorUrlKeys.paymentFrequency, state.paymentFrequency);
        url.searchParams.set(CalculatorUrlKeys.termAndRate, state.termAndRate.toString());
        url.searchParams.set(CalculatorUrlKeys.tradeInOwing, state[CalculatorFields.TradeInOwingAmount].toString());
    }
    else {
        url.searchParams.delete(CalculatorUrlKeys.downPayment);
        url.searchParams.delete(CalculatorUrlKeys.paymentFrequency);
        url.searchParams.delete(CalculatorUrlKeys.termAndRate);
        url.searchParams.delete(CalculatorUrlKeys.tradeInOwing);
    }
    window.history.pushState({}, '', url);
};
// Primary calculator short-hand methods
var selectPrimaryCalculator = function (state) { return state.financialCalculators[primaryCalculatorId]; };
export var selectPrimaryCalculatorIsPaymentMethodCash = createSelector([selectPrimaryCalculator], function (calculator) { return (calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod) === PaymentMethod.cash; });
export var selectPrimaryCalculatorIsPaymentMethodLease = createSelector([selectPrimaryCalculator], function (calculator) { return (calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod) === PaymentMethod.lease; });
export var selectPrimaryCalculatorAdditionalLeaseAnnualKmAllowance = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.additionalLeaseAnnualKmAllowance; });
export var selectPrimaryCalculatorLeaseAnnualKmAllowance = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.leaseAnnualKmAllowance; });
export var selectPrimaryCalculatorTermAndRate = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.termAndRate; });
export var selectPrimaryCalculatorPaymentFrequency = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.paymentFrequency; });
export var selectPrimaryCalculatorPaymentMethod = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod; });
export var selectPrimaryCalculatorDownPaymentAmount = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.downPaymentAmount; });
export var selectPrimaryCalculatorTradeInValueAmount = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.tradeInValueAmount; });
export var selectPrimaryCalculatorTradeInOwingAmount = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.tradeInOwingAmount; });
export var selectBrandDefaultPaymentOptions = function (state) { return state.brandConfig.paymentOptions; };
export var selectPrimaryCalculatorWarrantyKey = createSelector([selectPrimaryCalculator], function (calculator) { return calculator === null || calculator === void 0 ? void 0 : calculator.warrantyKey; });
export var selectPrimaryCalculatorPaymentOptions = createSelector(selectPrimaryCalculator, selectorForOffers, function (state, id) {
    if (id === void 0) { id = primaryCalculatorId; }
    return id === primaryCalculatorId;
}, function (calculator, offersState, isPrimary) { return generatePaymentOptions(calculator, offersState, isPrimary); });
export var selectModelCardPreferredPaymentOptions = createSelector([selectPrimaryCalculatorPaymentOptions], function (paymentOptions) { return (__assign(__assign({}, paymentOptions), { leaseAdditionalAnnualKm: calculatorInitialState.additionalLeaseAnnualKmAllowance, leaseAnnualKmAllowance: calculatorInitialState.leaseAnnualKmAllowance, downPaymentAmount: calculatorInitialState.downPaymentAmount, tradeInOwingAmount: calculatorInitialState.tradeInOwingAmount, tradeInValueAmount: calculatorInitialState.tradeInValueAmount, incentiveOfferIds: calculatorInitialState.incentiveOfferIds })); });
export var selectDefaultPaymentOptions = createSelector(selectBrandDefaultPaymentOptions, selectPrimaryCalculatorPaymentOptions, function (defaultPaymentOptions, paymentOptions) { return (__assign(__assign({}, paymentOptions), { leaseAdditionalAnnualKm: calculatorInitialState.additionalLeaseAnnualKmAllowance, leaseAnnualKmAllowance: calculatorInitialState.leaseAnnualKmAllowance, paymentMethod: defaultPaymentOptions.paymentMethod || calculatorInitialState.paymentMethod, paymentFrequency: defaultPaymentOptions.paymentFrequency || calculatorInitialState.paymentFrequency, termAndRate: defaultPaymentOptions.termAndRate || calculatorInitialState.termAndRate, downPaymentAmount: calculatorInitialState.downPaymentAmount, tradeInOwingAmount: calculatorInitialState.tradeInOwingAmount, tradeInValueAmount: calculatorInitialState.tradeInValueAmount, incentiveOfferIds: calculatorInitialState.incentiveOfferIds })); });
export var selectPrimaryCalculatorCashPaymentQueryStringParams = createSelector([selectPrimaryCalculator], function (calculator) { return generateCashQueryStringParams(calculator); });
export var selectPrimaryCalculatorLeaseFinancePaymentQueryStringParams = createSelector([selectPrimaryCalculator], function (calculator) { return generateLeaseFinanceQueryStringParams(calculator); });
export var selectPrimaryCalculatorQueryStringParams = createSelector([
    selectPrimaryCalculatorIsPaymentMethodCash,
    selectPrimaryCalculatorCashPaymentQueryStringParams,
    selectPrimaryCalculatorLeaseFinancePaymentQueryStringParams,
], function (isCashPayment, cashQuerystringParams, leaseFinanceQueryStringParams) {
    return isCashPayment ? cashQuerystringParams : leaseFinanceQueryStringParams;
});
export var selectPrimaryCalculatorQueryString = createSelector([selectPrimaryCalculatorQueryStringParams], function (calculatorQueryStringParams) { return generateQueryString(calculatorQueryStringParams); });
export var selectPrimaryCalculatorIncentiveOfferIds = createSelector([selectPrimaryCalculator], function (calculator) { var _a; return (_a = calculator === null || calculator === void 0 ? void 0 : calculator.incentiveOfferIds) !== null && _a !== void 0 ? _a : []; });
export var selectPrimaryCalculatorIncentiveOfferIdsParam = createSelector([selectPrimaryCalculatorIncentiveOfferIds], function (ids) { return ids.join(','); });
export var selectPrimaryCalculatorLoyaltyOfferKey = createSelector([selectPrimaryCalculator], function (calculator) { var _a; return (_a = calculator === null || calculator === void 0 ? void 0 : calculator.loyaltyOfferKey) !== null && _a !== void 0 ? _a : ''; });
export var primaryCalculatorPaymentMethodChanged = function (paymentMethod) {
    return function (dispatch, getState) {
        dispatch(setPaymentMethod({ id: primaryCalculatorId, paymentMethod: paymentMethod }));
        var defaultTerm = getState().brandConfig.paymentMethodDefaults[paymentMethod].term;
        dispatch(setTermAndRate({ id: primaryCalculatorId, termAndRate: defaultTerm }));
    };
};
export var paymentMethodChanged = function (calculatorId, paymentMethod) {
    return function (dispatch, getState) {
        var _a, _b, _c, _d, _e, _f, _g;
        var transmissionKey = (_b = (_a = getState()) === null || _a === void 0 ? void 0 : _a.currentVehicle) === null || _b === void 0 ? void 0 : _b.transmissionKey;
        var offerIds = ((_f = (_e = (_d = (_c = getState()) === null || _c === void 0 ? void 0 : _c.offers) === null || _d === void 0 ? void 0 : _d[transmissionKey]) === null || _e === void 0 ? void 0 : _e[paymentMethod]) === null || _f === void 0 ? void 0 : _f.incentive) || [];
        dispatch(setPaymentMethod({ id: calculatorId, paymentMethod: paymentMethod }));
        var defaultTerm = (_g = getState().brandConfig.paymentMethodDefaults[paymentMethod]) === null || _g === void 0 ? void 0 : _g.term;
        dispatch(setTermAndRate({ id: calculatorId, termAndRate: defaultTerm }));
        dispatch(offersSelectionChanged({ calculatorId: calculatorId, offerIds: offerIds }));
    };
};
export var createAndHydrateCalculator = function (sourceId, destinationId) {
    return function (dispatch, getState) {
        var _a, _b, _c, _d, _e, _f, _g;
        var transmissionKey = (_b = (_a = getState()) === null || _a === void 0 ? void 0 : _a.currentVehicle) === null || _b === void 0 ? void 0 : _b.transmissionKey;
        var paymentMethod = ((_d = (_c = getState()) === null || _c === void 0 ? void 0 : _c.financialCalculators[sourceId]) === null || _d === void 0 ? void 0 : _d.paymentMethod) || PaymentMethod.lease;
        var offers = (_g = (_f = (_e = getState()) === null || _e === void 0 ? void 0 : _e.offers) === null || _f === void 0 ? void 0 : _f[transmissionKey]) === null || _g === void 0 ? void 0 : _g[paymentMethod];
        dispatch(createCalculator(destinationId));
        dispatch(hydrateFromCalculator({
            sourceId: sourceId,
            destinationId: destinationId,
            offerIds: (offers === null || offers === void 0 ? void 0 : offers.incentive) || [],
            loyaltyOffer: (offers === null || offers === void 0 ? void 0 : offers.loyalty) || '',
        }));
    };
};
export var persistCalculatorToUrl = function (calculatorId) {
    return function (dispatch, getState) {
        var calculator = getState().financialCalculators[calculatorId];
        if (calculator) {
            updateQueryStringFromCalculator(calculator);
        }
    };
};
export var persistPrimaryCalculatorToUrl = function () { return function (dispatch, getState) {
    var calculator = getState().financialCalculators[primaryCalculatorId];
    if (calculator) {
        updateQueryStringFromCalculator(calculator);
    }
}; };
export var warrantyChangedForDraftCalculator = function (key, price) {
    return function (dispatch) {
        dispatch(draftCalculatorWarrantySelected({ key: key, price: price || 0 }));
    };
};
export var warrantyChangedForPrimaryCalculator = function (key, price, checkedModalWarrantyCard, checkedModalWarrantyKey) {
    return function (dispatch) {
        dispatch(primaryCalculatorWarrantySelected({ key: key, price: price || 0, checkedModalWarrantyCard: checkedModalWarrantyCard, checkedModalWarrantyKey: checkedModalWarrantyKey }));
        dispatch(persistPrimaryCalculatorToUrl());
    };
};
// Draft payment methods
var selectDraftCalculator = function (state) { return state.financialCalculators[defaultDraftCalculatorId]; };
export var selectDraftCalculatorIsPaymentMethodCash = createSelector([selectDraftCalculator], function (calculator) { return (calculator === null || calculator === void 0 ? void 0 : calculator.paymentMethod) === PaymentMethod.cash; });
/**
 * Used to signal fields to override on the `paymentOptions` object.\
 * See `src/components/ModelCardGrid/hooks/useModelCardsWithPaymentData.ts` for usage
 *
 * @example
 * const paymentOptions = {...defaultPaymentOptions, incentiveOfferIds: PAYMENT_OPTIONS_OVERRIDES.incentiveOfferIds}
 * // ...
 * const offerIds = paymentOptions.incentiveOfferIds
 * const incentiveOfferIds = isPaymentOptionsOverride('incentiveOfferIds', offerIds) ? [] : offerIds
 */
export var PAYMENT_OPTIONS_OVERRIDES = {
    incentiveOfferIds: [Infinity],
};
/**
 * Used to check if a field set on `paymentOptions` signals for its value to be overridden.
 *
 * @example
 * const paymentOptions = {...defaultPaymentOptions, incentiveOfferIds: PAYMENT_OPTIONS_OVERRIDES.incentiveOfferIds}
 * // ...
 * const offerIds = paymentOptions.incentiveOfferIds
 * const incentiveOfferIds = isPaymentOptionsOverride('incentiveOfferIds', offerIds) ? [] : offerIds
 */
export var isPaymentOptionsOverride = function (key, value) {
    switch (key) {
        case 'incentiveOfferIds':
            return value[0] === PAYMENT_OPTIONS_OVERRIDES[key][0];
        default:
            return false;
    }
};
export default financialCalculatorsReducer;
